import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import screens from './Screens.module.css';
import styles from './Account.module.css';
import { Badge, Button, Segmented } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext.jsx';
import { formatDate, useI18n } from '../../i18n/index.jsx';

/**
 * Profile, plan and preferences. Reads live data from /auth/me and
 * /api/web/subscription; signing out calls /auth/logout.
 */
export function Account() {
  const { t, lang, setLang, languages } = useI18n();
  const { user, subscription, tier, isPaid, signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    document.title = `${t('account_title')} — Don Quack`;
  }, [t]);

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
    navigate('/app/login', { replace: true });
  }

  const planBadge =
    tier === 'premium' && isPaid ? { tone: 'pro', label: t('sub_status_premium') }
    : tier === 'basic' && isPaid ? { tone: 'info', label: t('sub_status_basic') }
    : { tone: 'low', label: t('plan_free') };

  return (
    <section>
      <h2 className={screens.title}>{t('account_title')}</h2>
      <p className={screens.sub}>{t('account_subtitle')}</p>

      <div className={styles.section}>
        <h3>{t('account_profile')}</h3>
        <Row label={t('account_email')} value={user?.email ?? '—'} />
        <Row
          label={t('account_member_since')}
          value={
            user?.created_at
              ? formatDate(user.created_at, lang, { month: 'long', year: 'numeric' })
              : '—'
          }
        />
        <Row label={t('account_signed_in_via')} value={t('account_signed_in_email')} />
      </div>

      <div className={styles.section}>
        <h3>{t('account_subscription')}</h3>
        <Row
          label={t('account_current_plan')}
          value={<Badge tone={planBadge.tone}>{planBadge.label}</Badge>}
        />
        {isPaid && subscription?.expires_at && (
          <Row
            label={t('account_expires')}
            value={
              <>
                {formatDate(subscription.expires_at, lang, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
                {subscription.days_remaining !== null && (
                  <span className={styles.muted}>
                    {' '}
                    · {t('account_days_remaining', { days: subscription.days_remaining })}
                  </span>
                )}
              </>
            }
          />
        )}
        <Row
          label={t('nav_pricing')}
          value={
            <Button size="sm" to="/app/pricing">
              {t('account_see_plans')}
            </Button>
          }
        />
      </div>

      <div className={styles.section}>
        <h3>{t('account_language')}</h3>
        <Segmented
          options={languages.map((l) => ({ value: l.code, label: l.label }))}
          value={lang}
          onChange={setLang}
          ariaLabel={t('account_language')}
          className={styles.langSeg}
        />
        {/* The account-level language preference (POST /api/language) is
            Telegram-auth only today, so this choice lives in the browser and
            doesn't yet follow the user to another device. */}
      </div>

      <div className={styles.section}>
        <h3>{t('account_sign_out')}</h3>
        <Row
          label={t('account_sign_out')}
          value={
            <Button size="sm" variant="secondary" loading={signingOut} onClick={handleSignOut}>
              {t('account_sign_out_action')}
            </Button>
          }
        />
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className={styles.row}>
      <span className={styles.key}>{label}</span>
      <span className={styles.value}>{value}</span>
    </div>
  );
}
